import { computed, ref } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { email, helpers, minLength, required, sameAs } from '@vuelidate/validators';
import { useToast } from 'primevue/usetoast';
import { useRouter } from 'vue-router';

import { useRegisterUser } from '@/composables/useRegisterUser';
import { useAcceptInvitation } from '@/composables/useAcceptInvitation';
import { useFormErrors } from '@/composables/useFormErrors';
import { toUserMessage } from '@/utils/error';
import type { UserRegisterInput } from '@/types/composition-functions';

export const PASSWORD_MIN_LENGTH = 8;
export const EXISTING_ACCOUNT_MESSAGE = 'This email already has a Gradeverse account.';
export const ACCEPTED_MESSAGE = 'Invitation accepted. Log in with your existing password to continue.';

/**
 * Accept-invitation page. An invitee either creates an account, or — when the
 * invited email already has one (they work at another Gradeverse school) —
 * accepts with that account. acceptSchoolInvitation is authorised by the
 * emailed token plus, for an account whose email has not been verified, that
 * account's password: the token proves the inbox, the password proves the
 * account, so an account someone else registered under the address can't
 * take the invitation. The backend reports a missing or wrong password as a
 * `password` validation error, shown under the existing-account password field.
 */
export function useAcceptInvitationForm(token: string) {
  const toast = useToast();
  const router = useRouter();
  const { registerUser } = useRegisterUser();
  const { acceptInvitation } = useAcceptInvitation();
  const { serverErrors, handleServerErrors, clearErrors, getErrorMessage: serverError } = useFormErrors();
  // Kept apart from the create-account errors: both forms have a "password".
  const acceptErrors = useFormErrors();

  const form = ref<UserRegisterInput>({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    invitationToken: token,
    isAcceptingTokens: true,
  });

  const isSubmitting = ref(false);
  const isAccepting = ref(false);
  /** Set when the invited email is already registered, or the invitee says it is. */
  const hasExistingAccount = ref(false);
  /** The existing account's password, sent with the accept request. */
  const existingPassword = ref('');

  const existingPasswordError = computed(() => acceptErrors.getErrorMessage('password'));

  const password = computed(() => form.value.password);

  const rules = {
    form: {
      name: { required: helpers.withMessage('Name is required.', required) },
      email: {
        required: helpers.withMessage('A valid email is required.', required),
        email: helpers.withMessage('A valid email is required.', email),
      },
      password: {
        required: helpers.withMessage('Password is required.', required),
        minLength: helpers.withMessage(
          `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`,
          minLength(PASSWORD_MIN_LENGTH),
        ),
      },
      password_confirmation: {
        required: helpers.withMessage('Please confirm your password.', required),
        sameAsPassword: helpers.withMessage('Passwords must match.', sameAs(password)),
      },
    },
  };

  const v$ = useVuelidate(rules, { form });

  type FieldKey = keyof typeof rules.form;

  /** Client-side message first, then the server's message for that field. */
  function fieldError(field: FieldKey): string {
    const clientError = v$.value.form[field]?.$errors?.[0]?.$message;
    if (clientError) return String(clientError);
    return serverError(field);
  }

  async function createAccount(): Promise<boolean> {
    clearErrors();
    hasExistingAccount.value = false;
    if (!(await v$.value.$validate())) return false;

    isSubmitting.value = true;
    try {
      await registerUser({ ...form.value });
      return true;
    } catch (error) {
      handleServerErrors(error);
      // The email is pre-filled from the invitation and read-only, so an email
      // error here means the address is already registered.
      if (serverError('email')) {
        hasExistingAccount.value = true;
        return false;
      }
      if (Object.keys(serverErrors.value).length === 0) {
        toast.add({
          severity: 'error',
          summary: 'Account not created',
          detail: toUserMessage(error, 'We couldn\'t create your account. Please try again.'),
          life: 8000,
        });
      }
      return false;
    } finally {
      isSubmitting.value = false;
    }
  }

  /** Show the existing-account panel (password + accept) without registering. */
  function showExistingAccount(): void {
    hasExistingAccount.value = true;
  }

  async function acceptWithExistingAccount(): Promise<boolean> {
    acceptErrors.clearErrors();
    isAccepting.value = true;
    try {
      await acceptInvitation({ token, password: existingPassword.value || null });
      toast.add({ severity: 'success', summary: 'Invitation accepted', detail: ACCEPTED_MESSAGE, life: 6000 });
      await router.push('/login');
      return true;
    } catch (error) {
      acceptErrors.handleServerErrors(error);
      if (existingPasswordError.value) return false;
      toast.add({
        severity: 'error',
        summary: 'Invitation not accepted',
        detail: toUserMessage(error, 'We couldn\'t accept this invitation. Please try again or ask the school to resend it.'),
        life: 8000,
      });
      return false;
    } finally {
      isAccepting.value = false;
    }
  }

  return {
    form,
    v$,
    isSubmitting,
    isAccepting,
    hasExistingAccount,
    existingPassword,
    existingPasswordError,
    fieldError,
    createAccount,
    showExistingAccount,
    acceptWithExistingAccount,
  };
}
